import React, { useState, ChangeEvent, useRef, useEffect } from 'react';
import { useTheme } from '@aws-amplify/ui-react';
import { CheckboxField, Flex, Text, View, Button } from '@aws-amplify/ui-react';
import IcoPlus from '../image/ico_plus.svg';
import IcoMinus from '../image/ico_minus.svg';
import IcoInfo from '../image/ico_info.svg';
import { Tooltip } from 'react-tooltip';

export interface FacetsDataNode {
    name: string;
    hitcount: number | undefined;
    childDataDict: { [key: string]: FacetsDataNode };
}

interface CheckboxItemDataDict {
    [key: string]: FacetsDataNode;
}

interface ForManageCheckboxState {
    [key: string]: boolean;
}

interface ForManageSearchRequirement {
    [key: string]: string;
}

interface BooleanItem {
    [key: string]: boolean;
}

interface NumberItem {
    [key: string]: number;
}

export interface RegionItemNumType {
    prefecturecode: string;
    hitcount: number;
}

/*
    dataDict: チェックボックスリスト作成用の辞書型データ
    checkedItems: チェックされたチェックボックスの状態管理用辞書型データ
    setCheckedItems: checkedItemsのsetter
    forNotationItems: チェックされたdataDictのコードを「>」で連結して状態管理する用の辞書型データ
    setForNotationItems: forNotationItemsのsetter
    sortingList: 辞書型データは並び順が保証されていないため、並び順を固定したい場合に配列にデータのコードを表示したい順番で挿入する
*/
interface FilterFacetsCheckboxInputProps {
    dataDict: CheckboxItemDataDict;
    checkedItems: ForManageCheckboxState;
    setCheckedItems: (checkedItems: ForManageCheckboxState) => void;
    forNotationItems: ForManageSearchRequirement;
    setForNotationItems: (checkedItems:ForManageSearchRequirement) => void;
    sortingList: string[];
    upperLevelItemCodeConcatString: string | undefined;
    upperLevelItemNameConcatString: string | undefined;
    eachItemNum: {[key: string]: number};
    regionItemNum: {[key: string]: RegionItemNumType} | undefined;
    isTopLevelComponent: boolean;
    isRegionClassification: boolean;
    limit: number | 5 | null;
    isTopClass: boolean;
};

export const FilterFacetsCheckboxInput:React.FC<FilterFacetsCheckboxInputProps> = ({dataDict,checkedItems,setCheckedItems,forNotationItems,setForNotationItems,sortingList,upperLevelItemCodeConcatString,upperLevelItemNameConcatString,eachItemNum,regionItemNum,isTopLevelComponent,isRegionClassification,limit,isTopClass}) => {
    const { tokens } = useTheme();
    
    // 地域分類かつ親要素が東京都でないかつ区が最後につくデータ
    // または、地域分類かつ親要素が東京都であるかつ区または市が最後につくデータの場合trueにする
    const isWardData:boolean = isRegionClassification
        && (((Object.keys(dataDict)[0].substring(0,2) !== "13" && Object.keys(dataDict)[0] != "tokyoSpecialWards") && dataDict[Object.keys(dataDict)[0]]["name"].charAt(dataDict[Object.keys(dataDict)[0]]["name"].length - 1) === "区")
        || (Object.keys(dataDict)[0].substring(0,2) == "13" && (dataDict[Object.keys(dataDict)[0]]["name"].charAt(dataDict[Object.keys(dataDict)[0]]["name"].length - 1) === "区" || 
            dataDict[Object.keys(dataDict)[0]]["name"].charAt(dataDict[Object.keys(dataDict)[0]]["name"].length - 1) === "市")))

    const handleCheck:(itemId:string) => void = (itemId) => {
        if (!checkedItems[itemId] && !isChildDisplay[itemId]) {
            handleChildDisplay(itemId);
        }
        // 処理終了前にcheckedItemsにセットするための更新後データを入れる辞書型データ
        const newCheckedItems:ForManageCheckboxState = {...checkedItems};
        const newForNotationItems:ForManageSearchRequirement = {...forNotationItems};
        
        // チェックが外された際の処理
        // checkedItemsから下位階層のコードがキーであるを削除することによって、チェックを外す
        const unCheckChildren:(checkboxItemKey:string,checkboxItem:FacetsDataNode,isChecked:boolean,itemCodeConcatString:string) => void = (checkboxItemKey,checkboxItem,isChecked,itemCodeConcatString) => {
            delete newCheckedItems[checkboxItemKey];
            delete newForNotationItems[itemCodeConcatString];
            // 再帰させ一番下位の階層まで処理を行う
            if (Object.keys(checkboxItem.childDataDict).length > 0) {
                Object.keys(checkboxItem.childDataDict).forEach(key => {
                    unCheckChildren(key,checkboxItem.childDataDict[key],isChecked,itemCodeConcatString+">"+key);
                })
            }
        }
        
        const itemCodeConcatString = upperLevelItemCodeConcatString ? upperLevelItemCodeConcatString + ">" + itemId : itemId;
        const itemNameConcatString = upperLevelItemNameConcatString ? upperLevelItemNameConcatString + ">" + dataDict[itemId].name : dataDict[itemId].name;
        
        // チェックボックスのチェックをつけた場合はtrue,外した場合はfalse
        const isChecked = !checkedItems[itemId];
        
        if (isChecked) {
            newCheckedItems[itemId] = isChecked;
            newForNotationItems[itemCodeConcatString] = itemNameConcatString;      
            if (upperLevelItemCodeConcatString !== undefined && newForNotationItems[upperLevelItemCodeConcatString]) {
                delete newForNotationItems[upperLevelItemCodeConcatString]
            }
            if (!isTopClass && !isRegionClassification) {
                const parentItemId:string = itemId.substring(0,1) + ",00,000,0000";
                newCheckedItems[parentItemId] = isChecked;
            }
        } else {
            unCheckChildren(itemId,dataDict[itemId],isChecked,itemCodeConcatString);
            if (upperLevelItemCodeConcatString !== undefined && upperLevelItemNameConcatString !== undefined) {
                if(Object.keys(newForNotationItems).filter(key => key.includes(upperLevelItemCodeConcatString)).length === 0 || Object.keys(newForNotationItems).length === 0){
                    newForNotationItems[upperLevelItemCodeConcatString] = upperLevelItemNameConcatString;                    
                }
            }
        }

        // 更新後データをcheckedItemsにセットする
        setCheckedItems(newCheckedItems);
        setForNotationItems(newForNotationItems);
    }

    // データの要素が5つ以上ある場合は「もっと見る」を表示
    let [hasShowMoreList, setHasShowMoreList] = useState<BooleanItem>({});
    const [limitList, setLimitList] = useState<NumberItem>({});
    // 子要素がない場合はボタンを表示しない
    const [hasChildList, setHasChildList] = useState<BooleanItem>({});
    useEffect(() => {
        if (dataDict) {
            if (!sortingList.length || isRegionClassification) {
                const tmpShowMoreList: BooleanItem = Object.entries(dataDict).reduce((acc, [key,child]) => {
                    acc[key] = Object.keys(child.childDataDict).length > 5;
                    return acc;
                }, {} as BooleanItem);
                setHasShowMoreList(tmpShowMoreList);

                const tmpLimitList: NumberItem = {};
                Object.entries(dataDict).forEach(([key, child]) => {
                    const count = Object.keys(child.childDataDict).length;
                    tmpLimitList[key] = Math.min(count, 5);
                });
                setLimitList(tmpLimitList);
            } else {
                const tmpShowMoreList: BooleanItem = sortingList.reduce((acc, sortItem) => {
                    const child = dataDict[sortItem];
                    acc[sortItem] = child && Object.keys(child.childDataDict).length > 5;
                    return acc;
                }, {} as BooleanItem);
                setHasShowMoreList(tmpShowMoreList);

                const tmpLimitList: NumberItem = {};
                sortingList.forEach((sortItem) => {
                    const child = dataDict[sortItem];
                    const count = child && Object.keys(child.childDataDict).length;
                    tmpLimitList[sortItem] = Math.min(count, 5);
                });
                setLimitList(tmpLimitList);
            }
            
            const tmpChildList: BooleanItem = Object.entries(dataDict).reduce((acc, [key,child]) => {
                acc[key] = Object.keys(child.childDataDict).length > 0;
                return acc;
            }, {} as BooleanItem);
            setHasChildList(tmpChildList);
        }
    }, [dataDict]);
    const [isShowMore, setIsShowMore] = useState<BooleanItem>({});
    const handleShowMore:(itemId:string) => void = (itemId) => {
        setIsShowMore(prev => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
        setLimitList(prev => ({
            ...prev,
            [itemId]: Object.keys(dataDict[itemId].childDataDict).length,
        }));
    }

    // 子項目の表示制御
    const [isChildDisplay, setIsChildDisplay] = useState<BooleanItem>({});
    const handleChildDisplay:(itemId:string) => void = (itemId) => {
        // 「もっと見る」を戻す
        if (isChildDisplay[itemId]) {
            setIsShowMore(prev => ({
                ...prev,
                [itemId]: false,
            }));
            setLimitList(prev => ({
                ...prev,
                [itemId]: 5,
            }));
        }
        setIsChildDisplay(prev => ({
            ...prev,
            [itemId]: !prev[itemId],
        }));
    }
    
    //企業件数ラベルの作成
    const createCheckboxLabel = (
        name: string,
        key: string,
        isRegionClassification: boolean,
        eachItemNum: { [key: string]: number | undefined | null},
        hitcount: number | undefined,
    ): string => {
        const excludedNames = ["tokyoSpecialWards", "tokyoNonSpecialWards"];
        let count = null;
        if (isRegionClassification && excludedNames.includes(key)) {
            return name;
        }
        if (isRegionClassification && regionItemNum) {
            const target = Object.values(regionItemNum).find(item => item.prefecturecode === key.substring(0,2));
            count = target?.hitcount;
        } else {
            count = hitcount ?? eachItemNum[key];
        }
        return `${name}(${count !== null ? count : "0"})`;
    };
    
    return (
          <Flex direction="row" wrap="wrap" 
            marginLeft={{base:isTopLevelComponent ? "20px" : "30px", large:"20px"}} gap="0" 
            width="auto"
          >
            {/*sortingListが空でない場合、チェックボックス一覧表示時の並び替え順がsortingListの先頭から順になる*/}    
            {!sortingList.length ?
                (
                    <>
                        {(limit
                            ? Object.entries(dataDict).slice(0,limit)
                            : Object.entries(dataDict)
                        ).map(([key,value]) => 
                            <Flex direction="column" width="100%">
                                <Flex 
                                    marginTop="20px"
                                    width="100%"
                                    backgroundColor="transparent"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    gap="0px"
                                >
                                    <Flex flex="1">
                                        <CheckboxField                                            
                                            size="large"
                                            label={createCheckboxLabel(dataDict[key].name, key, isRegionClassification, eachItemNum, dataDict[key].hitcount)} 
                                            name={key} 
                                            value={key} 
                                            checked={checkedItems[key] !== undefined && checkedItems[key]} 
                                            onChange={() => handleCheck(key)} 
                                            gap="15px"
                                            paddingRight={{base:"0" ,large:"5px"}}
                                            data-tooltip-id={isTopClass || isRegionClassification ? "" : "displayMinorClass_" + key}
                                        />
                                    </Flex>
                                    <Tooltip id={"displayMinorClass_" + key} noArrow={true} clickable={true} opacity="1" positionStrategy="absolute" place="left-start" delayShow={20}
                                        style={{zIndex:"100", padding:"0", whiteSpace:"normal", fontSize:"1.4rem", lineHeight:"2.5rem", backgroundColor:"white", color:"black", borderRadius:"3px", boxShadow:"0 0.5rem 0.5rem gray"}}
                                    >
                                        <Flex direction="column">
                                            <Flex backgroundColor={tokens.colors.blue[20]} padding="10px 15px">
                                                <Text fontSize={tokens.fontSizes.xl} fontWeight="bold">業種の件数（詳細）</Text>
                                                <img data-tooltip-id="displayMinorClassTooltip" src={IcoInfo} height={"20px"} width={"20px"} alt=""/>
                                            </Flex>
                                            <Flex padding="3px 15px" direction="column">
                                                <Text padding="5px 10px" backgroundColor={tokens.colors.overlay[5]} fontSize={tokens.fontSizes.xl} fontWeight="bold" style={{borderBottom:"1.5px solid var(--amplify-colors-overlay-10)", borderTop:"1.5px solid var(--amplify-colors-overlay-10)"}}>選択した業種：{dataDict[key].name}</Text>
                                                <Flex direction="column" padding="0px 10px 20px" gap="5px">
                                                    {Object.entries(dataDict[key]["childDataDict"]).map(([childKey, childValue]) => 
                                                        (childValue.hitcount || (!childValue.hitcount && eachItemNum[childKey] > 0)) && (
                                                            <Flex direction="row">
                                                                ・{childValue.name}({childValue.hitcount ?? eachItemNum[childKey]})
                                                            </Flex>
                                                        )
                                                    )}
                                                </Flex>
                                            </Flex>
                                        </Flex>
                                    </Tooltip>
                                    <Flex as="button" className="plusMinusDisplaySwitchButton" marginTop="5px" marginLeft={{base:"5px", large:"10px"}} marginRight="15px" style={{cursor:"pointer"}} display={isTopClass && hasChildList ? "flex" : "none"} onClick={() => handleChildDisplay(key)}>
                                        {!isChildDisplay[key] ? <img src={IcoPlus} height="20px" width="20px"/> : <img src={IcoMinus} height="20px" width="20px"/>}
                                    </Flex>
                                </Flex>
                                {isTopClass && isChildDisplay[key] && dataDict[key]["childDataDict"] && Object.keys(dataDict[key]["childDataDict"]).length > 0 &&(
                                    <Flex direction="column">
                                        <FilterFacetsCheckboxInput 
                                            dataDict={dataDict[key]["childDataDict"]} 
                                            checkedItems={checkedItems} 
                                            setCheckedItems={setCheckedItems} 
                                            forNotationItems={forNotationItems} 
                                            setForNotationItems={setForNotationItems} 
                                            sortingList={[]} 
                                            upperLevelItemCodeConcatString = {upperLevelItemCodeConcatString ? upperLevelItemCodeConcatString + ">" + key : key}
                                            upperLevelItemNameConcatString = {upperLevelItemNameConcatString ? upperLevelItemNameConcatString + ">" + dataDict[key].name : dataDict[key].name}
                                            eachItemNum={eachItemNum}
                                            regionItemNum={undefined}
                                            isTopLevelComponent={false}
                                            isRegionClassification={isRegionClassification}
                                            limit={limitList[key]}
                                            isTopClass={false}
                                        />
                                        <Flex width="100%" justifyContent="flex-end">
                                            <Button tabIndex={0} border="none" marginTop="10px" paddingRight="20px" display={(hasShowMoreList[key] && !isShowMore[key]) ? "flex" : "none"} style={{cursor:"pointer"}} onClick={() => handleShowMore(key)}>
                                                <Text fontSize="14px" fontWeight="bold">すべて表示</Text>
                                            </Button>
                                        </Flex>
                                    </Flex>
                                )}
                            </Flex>
                        )}
                    </>
                )
                :
                (
                    <>
                        {(limit
                            ? sortingList.filter((sortingItem) => dataDict[sortingItem]).slice(0,limit)
                            : sortingList.filter((sortingItem) => dataDict[sortingItem])
                        ).map((sortingItem:string,index:number) =>
                            dataDict[sortingItem] && (
                                <Flex direction="column" width="100%">
                                    <Flex 
                                        marginTop="20px"
                                        width="100%"
                                        backgroundColor="transparent"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        gap="0px"
                                        data-tooltip-id={isTopClass || isRegionClassification ? "" : "displayMinorClass_" + sortingItem}
                                    >
                                        <Flex flex="1">
                                            <CheckboxField
                                                size="large"
                                                label={createCheckboxLabel(dataDict[sortingItem].name, sortingItem, isRegionClassification, eachItemNum, dataDict[sortingItem].hitcount)}
                                                name={sortingItem}
                                                value={sortingItem}
                                                checked={checkedItems[sortingItem] !== undefined && checkedItems[sortingItem]}
                                                onChange={() => handleCheck(sortingItem)}
                                                gap="15px"
                                                paddingRight={{base:"0" ,large:"5px"}}
                                            />
                                        </Flex>
                                        <Tooltip id={"displayMinorClass_" + sortingItem} noArrow={true} clickable={true} opacity="1" positionStrategy="absolute" place="left-start" delayShow={20}
                                            style={{zIndex:"100", padding:"0", whiteSpace:"normal", fontSize:"1.4rem", lineHeight:"2.5rem", backgroundColor:"white", color:"black", borderRadius:"3px", boxShadow:"0 0.5rem 0.5rem gray"}}
                                        >
                                            <Flex direction="column">
                                                <Flex backgroundColor={tokens.colors.blue[20]} padding="10px 15px">
                                                    <Text fontSize={tokens.fontSizes.xl} fontWeight="bold">業種の件数（詳細）</Text>
                                                    <img data-tooltip-id="displayMinorClassTooltip" src={IcoInfo} height={"20px"} width={"20px"} alt=""/>
                                                </Flex>
                                                <Flex padding="3px 15px" direction="column">
                                                    <Text padding="5px 10px" backgroundColor={tokens.colors.overlay[5]} fontSize={tokens.fontSizes.xl} fontWeight="bold" style={{borderBottom:"1.5px solid var(--amplify-colors-overlay-10)", borderTop:"1.5px solid var(--amplify-colors-overlay-10)"}}>選択した業種：{dataDict[sortingItem].name}</Text>
                                                    <Flex direction="column" padding="0px 10px 20px" gap="5px">
                                                        {sortingList.filter((sortingChildItem) => dataDict[sortingItem]["childDataDict"][sortingChildItem]).map((sortingChildItem:string,childIndex:number) =>
                                                            (dataDict[sortingItem]["childDataDict"][sortingChildItem] && (dataDict[sortingItem]["childDataDict"][sortingChildItem].hitcount || (!dataDict[sortingItem]["childDataDict"][sortingChildItem].hitcount && eachItemNum[sortingChildItem] > 0))) && (
                                                                <Flex direction="row">
                                                                    ・{dataDict[sortingItem]["childDataDict"][sortingChildItem].name}({dataDict[sortingItem]["childDataDict"][sortingChildItem].hitcount ?? eachItemNum[sortingChildItem]})
                                                                </Flex>
                                                            )
                                                        )}
                                                    </Flex>
                                                </Flex>
                                            </Flex>
                                        </Tooltip>
                                        <Flex as="button" className="plusMinusDisplaySwitchButton" marginTop="5px" marginLeft={{base:"5px", large:"10px"}} marginRight="15px" style={{cursor:"pointer"}} display={isTopClass && hasChildList ? "flex" : "none"} onClick={() => handleChildDisplay(sortingItem)}>
                                            {!isChildDisplay[sortingItem] ? <img src={IcoPlus} height="20px" width="20px"/> : <img src={IcoMinus} height="20px" width="20px"/>}
                                        </Flex>
                                    </Flex>
                                    {isTopClass && isChildDisplay[sortingItem] && dataDict[sortingItem]["childDataDict"] && Object.keys(dataDict[sortingItem]["childDataDict"]).length > 0 &&(
                                        <Flex direction="column">
                                            <FilterFacetsCheckboxInput
                                                dataDict={dataDict[sortingItem]["childDataDict"]} 
                                                checkedItems={checkedItems}
                                                setCheckedItems={setCheckedItems} 
                                                forNotationItems={forNotationItems} 
                                                setForNotationItems={setForNotationItems} 
                                                sortingList={isRegionClassification ? [] : sortingList}
                                                upperLevelItemCodeConcatString = {upperLevelItemCodeConcatString ? upperLevelItemCodeConcatString + ">" + sortingItem : sortingItem}
                                                upperLevelItemNameConcatString = {upperLevelItemNameConcatString ? upperLevelItemNameConcatString + ">" + dataDict[sortingItem].name : dataDict[sortingItem].name}
                                                eachItemNum={eachItemNum}
                                                regionItemNum={undefined}
                                                isTopLevelComponent={false}
                                                isRegionClassification={isRegionClassification}
                                                limit={limitList[sortingItem]}
                                                isTopClass={false}
                                            />
                                            <Flex width="100%" justifyContent="flex-end">
                                                <Button tabIndex={0} border="none" marginTop="10px" paddingRight="20px" display={(hasShowMoreList[sortingItem] && !isShowMore[sortingItem]) ? "flex" : "none"} style={{cursor:"pointer"}} onClick={() => handleShowMore(sortingItem)}>
                                                    <Text fontSize="14px" fontWeight="bold">すべて表示</Text>
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    )}
                                </Flex>
                            )
                        )}
                    </>
                )
            }
            <Tooltip id="displayMinorClassTooltip" noArrow={true} clickable={true} opacity="1" positionStrategy="absolute" place="top" delayShow={20}
            style={{maxWidth:"90vw", zIndex:"999", padding:"0.5rem", whiteSpace:"normal", fontSize:"1.2rem", lineHeight:"2.5rem", backgroundColor:"var(--color-blue-d)", color:"white", borderRadius:"3px", boxShadow:"0 0.1rem 0.1rem gray"}}
            >
                <p>選択した中分類の詳細な分類（小分類）の件数内訳を表示します。<br/>※小分類の選択および絞込みは不可。</p>
            </Tooltip>
          </Flex>    
    )
}